/**
 * ConnectorsView -- admin connectors grid (P40.2).
 *
 * Route: /admin/connectors
 *
 * Surfaces every Connector with last-run state + RAG status pill +
 * cadence + one-shot trigger button. Click-through to the audit log
 * filtered by this connector + expand-on-click drawer showing the
 * last 10 runs. Admin-only.
 *
 * RAG rules:
 *   error_streak > 0          -> red    (last run failed)
 *   no last_succeeded_at      -> slate  (never run)
 *   age >  2 * cadence_seconds -> amber  (stale)
 *   else                      -> green  (recent success)
 */
import { formatDateTime } from "shared/time/formatDate";
import { groupedCount, i18n } from "shared/i18n";
import { InfoTip } from "shared/components/Explainer";
import { Fragment, useMemo, useState } from "react";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { LiveRunsPanel } from "shared/admin/LiveRunsPanel";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { useSetupGuides } from "shared/api/hooks/useSetupGuides";
import { useDataSources } from "shared/api/hooks/useDataSources";
import { EscalationLine } from "shared/components/EscalationLine";
import { escalationTarget, needsRerun } from "shared/admin/connectorNudge";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import {
  useConnectorRuns,
  useConnectors,
  useTriggerConnectorRun,
} from "shared/api/hooks/useConnectors";
import { useBulkUpdateConnectors } from "shared/api/hooks/useBulkUpdateConnectors";
import {
  useClearConnectorCredential,
  useConnectorCredentials,
  useSetConnectorCredential,
  useTestConnector,
} from "shared/api/hooks/useConnectorCredentials";
import { EngineBusy } from "shared/components/EngineBusy";
import {
  useToggleConnectorEnabled,
  useUpdateConnector,
} from "shared/api/hooks/useToggleConnectorEnabled";
import { useToast } from "shared/toast/ToastProvider";
import type {
  ConnectorCredentialField,
  ConnectorRow,
  ConnectorTestResult,
} from "shared/api/types";

import {
  formatCadence,
  formatRelative,
  needsAttention,
  ragStateFor,
  stateReason,
  type RagState,
} from "./connectorsHelpers";

import { Identifier } from "shared/components/Identifier";
import { Figure } from "shared/components/Figure";
const RAG_TONE: Record<RagState, string> = {
  red: "border-rose-300 bg-rose-50 text-rose-900",
  amber: "border-amber-300 bg-amber-50 text-amber-900",
  green: "border-emerald-300 bg-emerald-50 text-emerald-900",
  slate: "border-slate-300 bg-slate-50 text-slate-700",
};

const RAG_LABEL: Record<RagState, string> = {
  red: "failing",
  amber: "stale",
  green: "fresh",
  slate: "never run",
};

export function ConnectorsView() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;

  const connectors = useConnectors({ enabled: isAdmin });
  const trigger = useTriggerConnectorRun();
  const toggleEnabled = useToggleConnectorEnabled();
  const bulkUpdate = useBulkUpdateConnectors();
  const toast = useToast();
  const navigate = useNavigate();

  // D3 -- "1 need attention (Architecture edges)" was TEXT. The
  // product knew which connector and made the reader find it in a
  // fourteen-row grid. `?connector=` opens the row that was named.
  const [params] = useSearchParams();
  const [expandedId, setExpandedId] = useState<string | null>(params.get("connector"));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const allRows = useMemo(() => connectors.data?.connectors ?? [], [connectors.data]);
  /* Round 10 F6 -- an administrator arriving from the strip's "1 need
     attention" link read fourteen rows to find the one he was sent
     for, third round. The product already knows which rows those are
     (`needsAttention`, which the strip itself counts with); the table
     simply never offered to show only them. Sorting is not enough on
     its own -- a fourteen-row table sorted by state still shows
     fourteen rows -- so this filters, says how many it is hiding, and
     is off by default because the register is also how somebody reads
     the whole estate. */
  const [attentionOnly, setAttentionOnly] = useState(params.get("attention") === "1");
  const attentionRows = useMemo(() => needsAttention(allRows), [allRows]);
  const rows = attentionOnly ? attentionRows : allRows;

  const visibleSelectedIds = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)).map((r) => r.id),
    [rows, selectedIds],
  );
  const selectedCount = visibleSelectedIds.length;
  const allVisibleSelected = rows.length > 0 && selectedCount === rows.length;
  // H -- how many of them are actually switched on.
  const enabledCount = rows.filter((r) => r.enabled).length;

  if (!isAdmin) {
    return <AdminOnlyPanel surface="Connectors" />;
  }

  const onTrigger = (row: ConnectorRow) => {
    trigger.mutate(row.id, {
      onSuccess: () => {
        toast.push({
          tone: "success",
          message: `Queued: ${row.label}`,
          detail: `Connector kind: ${row.kind}`,
        });
      },
      onError: (err) => {
        toast.push({
          tone: "error",
          message: `Trigger failed: ${row.label}`,
          detail: err.message,
        });
      },
    });
  };

  const onToggleEnabled = (row: ConnectorRow) => {
    const next = !row.enabled;
    toggleEnabled.mutate(
      { connectorId: row.id, enabled: next },
      {
        onSuccess: (data) => {
          if (data.enabled_changed) {
            toast.push({
              tone: next ? "success" : "warning",
              message: next ? `Enabled: ${row.label}` : `Disabled: ${row.label}`,
              detail: `Connector kind: ${row.kind}`,
            });
          }
        },
        onError: (err) => {
          toast.push({
            tone: "error",
            message: `Toggle failed: ${row.label}`,
            detail: err.message,
          });
        },
      },
    );
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const r of rows) next.delete(r.id);
        return next;
      }
      const next = new Set(prev);
      for (const r of rows) next.add(r.id);
      return next;
    });
  };

  const onBulkFlip = (enabled: boolean) => {
    if (visibleSelectedIds.length === 0) return;
    bulkUpdate.mutate(
      { connector_ids: visibleSelectedIds, enabled },
      {
        onSuccess: (data) => {
          const verb = enabled ? "enabled" : "disabled";
          toast.push({
            tone: data.changed > 0 ? (enabled ? "success" : "warning") : "info",
            message: `Bulk ${verb}: ${data.changed} changed`,
            detail:
              `${data.unchanged} already ${verb}` +
              (data.not_found_ids.length > 0
                ? `, ${data.not_found_ids.length} not found`
                : ""),
          });
          setSelectedIds(new Set());
        },
        onError: (err) => {
          toast.push({
            tone: "error",
            message: `Bulk update failed`,
            detail: err.message,
          });
        },
      },
    );
  };

  const onCadenceUpdate = (row: ConnectorRow, next: number | null) => {
    toggleEnabled.mutate(
      { connectorId: row.id, cadence_seconds: next },
      {
        onSuccess: (data) => {
          if (data.cadence_seconds_changed) {
            const oldCadence = formatCadence(row.cadence_seconds, t);
            const newCadence = formatCadence(data.cadence_seconds, t);
            toast.push({
              tone: "success",
              message: `Cadence updated: ${row.label}`,
              detail: `${oldCadence} -> ${newCadence}`,
            });
          }
        },
        onError: (err) => {
          toast.push({
            tone: "error",
            message: `Cadence update failed: ${row.label}`,
            detail: err.message,
          });
        },
      },
    );
  };

  return (
    <div data-testid="connectors-view" className="flex flex-col gap-4 p-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-slate-900">
          <MarsadIcon
            name="telemetry"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.connectorsView.connectors")}
        </h1>
        <p className="text-xs text-slate-600">
          {t("screens.connectorsView.everyRecurringDataFeedWiredToThe")}
        </p>
      </header>
      <LiveRunsPanel />

      <section
        data-testid="connectors-results"
        className="rounded border border-slate-200 bg-white"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 text-xs text-slate-600">
          {/* ROUND 13 H -- "13 connectors" is what the migrations
              seed, so the count cannot discriminate: it read the same
              on a morning when nothing was switched on as it would on
              a fully wired estate. Say how many are ON beside it --
              and say it through the catalogue, because an inline
              template literal with a hand-rolled plural is a sentence
              no translator can reach. */}
          <span data-testid="connectors-count" data-enabled={enabledCount}>
            {connectors.isLoading
              ? t("screens.connectorsView.loading")
              : t("screens.connectorsView.countWithEnabled", {
                  count: rows.length,
                  enabled: enabledCount,
                })}
          </span>
          {attentionRows.length > 0 && (
            <button
              type="button"
              data-testid="connectors-attention-filter"
              aria-pressed={attentionOnly}
              onClick={() => setAttentionOnly(!attentionOnly)}
              className={`rounded border px-2 py-0.5 ${
                attentionOnly
                  ? "border-amber-400 bg-amber-100 text-amber-900"
                  : "border-slate-300 bg-white text-slate-700"
              }`}
            >
              {attentionOnly
                ? t("screens.connectorsView.attentionShowAll", {
                    count: allRows.length,
                  })
                : t("screens.connectorsView.attentionOnly", {
                    count: attentionRows.length,
                  })}
            </button>
          )}
          {/* D5 -- the guided setup was reachable only from Bank Setup,
              and only while NO connector was enabled. Connecting the
              second system had no route at all. */}
          <Link
            to="/admin/connections/setup"
            data-testid="connectors-connect-a-system"
            className="rounded border border-brand-brassDark bg-brand-brass px-2 py-0.5 font-semibold text-brand-navy"
          >
            {t("screens.connectorsView.connectASystem")}
          </Link>
          <button
            type="button"
            data-testid="connectors-refresh"
            onClick={() => void connectors.refetch()}
            className="rounded border border-slate-300 bg-white px-2 py-0.5"
          >
            {t("screens.connectorsView.refresh")}
          </button>
        </div>

        {selectedCount > 0 && (
          <div
            data-testid="connectors-bulk-toolbar"
            className="flex items-center justify-between border-b border-indigo-200 bg-indigo-50 px-3 py-2 text-xs"
          >
            <span data-testid="connectors-bulk-count" className="text-indigo-900">
              {selectedCount} {t("screens.connectorsView.selected")}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="connectors-bulk-enable"
                disabled={bulkUpdate.isPending}
                onClick={() => onBulkFlip(true)}
                className="rounded border border-emerald-400 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-900 disabled:opacity-50"
              >
                {bulkUpdate.isPending
                  ? t("screens.connectorsView.updating")
                  : t("screens.connectorsView.enableSelected")}
              </button>
              <button
                type="button"
                data-testid="connectors-bulk-disable"
                disabled={bulkUpdate.isPending}
                onClick={() => onBulkFlip(false)}
                className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 font-semibold text-amber-900 disabled:opacity-50"
              >
                {bulkUpdate.isPending
                  ? t("screens.connectorsView.updating")
                  : t("screens.connectorsView.disableSelected")}
              </button>
              <button
                type="button"
                data-testid="connectors-bulk-clear"
                onClick={() => setSelectedIds(new Set())}
                className="rounded border border-slate-300 bg-white px-2 py-0.5 font-semibold text-slate-700"
              >
                {t("screens.connectorsView.clear")}
              </button>
            </div>
          </div>
        )}

        {connectors.error && (
          <p data-testid="connectors-error" className="px-3 py-4 text-xs text-rose-700">
            {t("screens.connectorsView.failedToLoad")}{" "}
            {connectors.error instanceof Error
              ? connectors.error.message
              : t("screens.connectorsView.unknown")}
          </p>
        )}

        {!connectors.error && rows.length === 0 && !connectors.isLoading && (
          <p
            data-testid="connectors-empty"
            className="px-3 py-4 text-xs italic text-slate-500"
          >
            {t("screens.connectorsView.noConnectorsRegisteredYet")}
          </p>
        )}

        {rows.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-start">
                <th className="px-2 py-1">
                  <input
                    type="checkbox"
                    data-testid="connectors-select-all"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all connectors"
                  />
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.connectorsView.status")}
                  <InfoTip termKey="connector" />
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.connectorsView.source")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.connectorsView.kind")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.connectorsView.cadence")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.connectorsView.lastRun")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.connectorsView.lastSuccess")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.connectorsView.errors")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rag = ragStateFor(row);
                const isExpanded = expandedId === row.id;
                return (
                  <ConnectorRowFragment
                    key={row.id}
                    row={row}
                    rag={rag}
                    isExpanded={isExpanded}
                    isSelected={selectedIds.has(row.id)}
                    isTriggering={trigger.isPending && trigger.variables === row.id}
                    isToggling={
                      toggleEnabled.isPending &&
                      toggleEnabled.variables?.connectorId === row.id
                    }
                    onToggleSelect={() => toggleSelected(row.id)}
                    onToggleExpand={() => setExpandedId(isExpanded ? null : row.id)}
                    onTrigger={() => onTrigger(row)}
                    onToggleEnabled={() => onToggleEnabled(row)}
                    onCadenceUpdate={(v) => onCadenceUpdate(row, v)}
                    onViewAudit={() =>
                      navigate(
                        `/admin/audit-events?entity_type=connector&entity_id=${row.id}`,
                      )
                    }
                  />
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

interface RowFragmentProps {
  row: ConnectorRow;
  rag: RagState;
  isExpanded: boolean;
  isSelected: boolean;
  isTriggering: boolean;
  isToggling: boolean;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  onTrigger: () => void;
  onToggleEnabled: () => void;
  onCadenceUpdate: (next: number | null) => void;
  onViewAudit: () => void;
}

function ConnectorRowFragment({
  row,
  rag,
  isExpanded,
  isSelected,
  isTriggering,
  isToggling,
  onToggleSelect,
  onToggleExpand,
  onTrigger,
  onToggleEnabled,
  onCadenceUpdate,
  onViewAudit,
}: RowFragmentProps) {
  const { t } = useTranslation();
  const disabled = !row.enabled;
  // E5 -- who owns this feed. Bank Setup's data-source register
  // records an owner per source; naming them beats "an administrator".
  const sources = useDataSources();
  // Round 10 F6 -- the human name for this connector kind, from the
  // setup guide that already holds one for every kind.
  const guides = useSetupGuides();
  // Round 17 H35 -- a kind with no setup guide (the evidence freshness
  // probe, which needs no credentials) fell back to its bare code; its
  // source system already carries a name.
  const systemName =
    (guides.data ?? []).find((g) => g.kind === row.kind)?.system ??
    row.source_system_label ??
    null;
  const sourceOwner =
    (sources.data ?? []).find(
      (d) => d.name === row.source_system_label || d.name === row.source_system_code,
    )?.owner ?? null;
  const me = useMe();
  const escalation = escalationTarget(sourceOwner, me.data?.is_admin ?? false);
  const rerun = needsRerun({
    settingsChangedAt: row.settings_changed_at,
    lastRunAt: row.last_run_at,
    lastRunStatus: row.last_run_status,
  });
  return (
    <>
      <tr
        data-testid="connectors-row"
        data-connector-id={row.id}
        data-connector-kind={row.kind}
        data-rag={rag}
        data-enabled={row.enabled}
        data-selected={isSelected}
        className={`cursor-pointer border-b border-slate-100 hover:bg-slate-50 ${disabled ? "opacity-60" : ""}`}
        onClick={onToggleExpand}
      >
        <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            data-testid="connectors-row-select"
            checked={isSelected}
            onChange={onToggleSelect}
            aria-label={`Select ${row.label}`}
          />
        </td>
        <td className="px-2 py-1">
          {disabled ? (
            <span
              data-testid="connectors-row-disabled-pill"
              className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-900"
            >
              {t("screens.connectorsView.disabled")}
            </span>
          ) : (
            <span
              data-testid="connectors-row-rag"
              className={`rounded border px-2 py-0.5 text-[10px] font-semibold ${RAG_TONE[rag]}`}
            >
              {RAG_LABEL[rag]}
            </span>
          )}
        </td>
        {/* E2 -- isolate the VALUE, not the cell.
            The cell used to carry the isolation through its font-mono
            class. E1 took that away on purpose -- a cell taking
            direction: ltr made its `text-end` mean the opposite edge
            from its own header -- and the value needed somewhere to
            get it back. A component per kind of value is what stops
            this landing instance by instance again. */}
        <td className="px-2 py-1 text-slate-700">
          <Identifier>{row.source_system_code}</Identifier>
        </td>
        {/* Round 10 F6 -- the column printed a bare code string,
            fourth round. The product has held a human name for every
            connector kind since P383 (the setup guide's `system`), and
            this table never asked for it. The code stays beneath it:
            it is what the operator matches against a config file and
            against our own documentation. */}
        <td className="px-2 py-1 text-slate-800">
          {systemName ? (
            <>
              <span data-testid={`connector-system-${row.kind}`}>{systemName}</span>
              <span className="block text-[10px] text-slate-400">
                <Identifier>{row.kind}</Identifier>
              </span>
            </>
          ) : (
            <Identifier>{row.kind}</Identifier>
          )}
        </td>
        <td className="px-2 py-1 text-slate-700">
          <CadenceCell
            row={row}
            isToggling={isToggling}
            onCadenceUpdate={onCadenceUpdate}
          />
        </td>
        {/* D5 -- this column showed the last SUCCESS under the heading
            "Last run", so a connector with 588 failed runs and no
            successes read "Last run: never" beside "Errors: 588". */}
        <td className="px-2 py-1 text-slate-700" data-testid="connector-last-run">
          {formatRelative(row.last_run_at ?? null, Date.now(), t)}
          {row.last_run_status && (
            <span
              data-run-status={row.last_run_status}
              className={`ms-1 text-[10px] ${
                row.last_run_status === "succeeded" ? "text-slate-400" : "text-rose-700"
              }`}
            >
              ({row.last_run_status})
            </span>
          )}
        </td>
        <td className="px-2 py-1 text-slate-700" data-testid="connector-last-success">
          {formatRelative(row.last_succeeded_at, Date.now(), t)}
        </td>
        <td className="px-2 py-1">
          {row.error_streak > 0 ? (
            <span className="font-semibold text-rose-700">{row.error_streak}</span>
          ) : (
            <span className="text-slate-400">{0}</span>
          )}
        </td>
        <td className="px-2 py-1">
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              data-testid="connectors-row-trigger"
              disabled={!row.enabled || isTriggering}
              onClick={(e) => {
                e.stopPropagation();
                onTrigger();
              }}
              className="rounded border border-emerald-400 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-900 disabled:opacity-50"
            >
              {isTriggering
                ? t("screens.connectorsView.queueing")
                : t("screens.connectorsView.runNow")}
            </button>
            <button
              type="button"
              data-testid="connectors-row-toggle-enabled"
              data-target-enabled={!row.enabled}
              disabled={isToggling}
              onClick={(e) => {
                e.stopPropagation();
                onToggleEnabled();
              }}
              className={`rounded border px-2 py-0.5 text-[10px] font-semibold disabled:opacity-50 ${
                row.enabled
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : "border-emerald-400 bg-emerald-50 text-emerald-900"
              }`}
            >
              {isToggling
                ? t("screens.connectorsView.updating")
                : row.enabled
                  ? t("screens.connectorsView.disable")
                  : t("screens.connectorsView.enable")}
            </button>
            <button
              type="button"
              data-testid="connectors-row-view-audit"
              onClick={(e) => {
                e.stopPropagation();
                onViewAudit();
              }}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700"
            >
              {t("screens.connectorsView.audit")}
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr data-testid="connectors-row-expanded">
          <td colSpan={9} className="bg-slate-50 px-2 py-2">
            {/* D5 -- why the row is that colour, including the case
                the walkthrough hit: Run now returned 202 and nothing
                ever started. */}
            {stateReason(row, Date.now(), t) && (
              <p
                data-testid={`connector-reason-${row.id}`}
                className="mb-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
              >
                {stateReason(row, Date.now(), t)}
              </p>
            )}
            {/* E5 -- the panel described the fault and stopped. The
                operator's next action is always to tell somebody, and
                the data-source register knows who owns this feed. */}
            {/* D2 -- a repaired connector showed the new configuration
                beside the old error and offered nothing, so the person
                who had just fixed it had no way to tell whether it had
                taken. Fourth round. */}
            {rerun && (
              <p
                data-testid={`connector-rerun-nudge-${row.id}`}
                className="mb-2 flex flex-wrap items-center gap-2 rounded border border-brand-brassLine bg-brand-brassTint px-2 py-1 text-[11px] text-ink"
              >
                <span>{t("screens.connectorsView.settingsChangedSinceRun")}</span>
                <button
                  type="button"
                  data-testid={`connector-rerun-now-${row.id}`}
                  onClick={onTrigger}
                  className="font-semibold text-brand-brassDark hover:underline"
                >
                  {t("screens.connectorsView.runNowToCheck")}
                </button>
              </p>
            )}
            {/* D2 -- and the setup guide, which is where the answer to
                "what does this connector need" already lives. The panel
                described a fault and pointed nowhere. */}
            {stateReason(row, Date.now(), t) && (
              <p className="mb-2 text-[11px]">
                <Link
                  data-testid={`connector-guide-${row.id}`}
                  to={`/admin/connections/setup?kind=${row.kind}`}
                  className="text-brand-brassDark hover:underline"
                >
                  {t("screens.connectorsView.openGuide")}
                </Link>
              </p>
            )}
            {stateReason(row, Date.now(), t) && escalation.isSelf && (
              // D2 -- it named the platform administrator TO the
              // platform administrator. Telling somebody to escalate to
              // himself reads as a dead end, where the real answer is
              // that this feed has no recorded owner -- a gap he can
              // close in Bank Setup.
              <p
                data-testid={`connector-no-owner-${row.id}`}
                className="mb-2 text-[11px] text-slate-600"
              >
                {t("screens.connectorsView.noOwnerRecorded")}{" "}
                <Link
                  to="/admin/bank-setup"
                  className="font-medium text-brand-brassDark hover:underline"
                >
                  {t("screens.connectorsView.recordAnOwner")}
                </Link>
              </p>
            )}
            {stateReason(row, Date.now(), t) && escalation.who && (
              <EscalationLine
                testid={`connector-escalate-${row.id}`}
                who={escalation.who}
                details={{
                  what: `Connector "${row.label}" (${row.kind}) needs attention`,
                  context: {
                    "Source system": row.source_system_code,
                    Enabled: String(row.enabled),
                    "Consecutive errors": row.error_streak,
                    "Last run": row.last_run_at ?? "never",
                    "Last run outcome": row.last_run_status ?? "n/a",
                    "Last success": row.last_succeeded_at ?? "never",
                    "Last error": row.last_error_message ?? "",
                  },
                }}
              />
            )}
            {row.last_error_message && (
              <div className="mb-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-800">
                <span className="font-semibold">
                  {t("screens.connectorsView.lastError")}
                </span>{" "}
                {row.last_error_message}
              </div>
            )}
            <ConnectorCredentialsCell row={row} />
            <ConnectorConfigCell row={row} />
            <ConnectorRunHistory
              connectorId={row.id}
              rowVersion={`${row.last_run_at ?? ""}|${row.last_run_status ?? ""}|${row.error_streak}`}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ConnectorRunHistory({
  connectorId,
  rowVersion,
}: {
  connectorId: string;
  /** The row's own last-run marker; see useConnectorRuns. */
  rowVersion: string;
}) {
  const { t } = useTranslation();
  const history = useConnectorRuns(connectorId, { limit: 10, rowVersion });
  if (history.isLoading) {
    return (
      <p className="text-[11px] italic text-slate-500">
        {t("screens.connectorsView.loadingRunHistory")}
      </p>
    );
  }
  if (history.error) {
    return (
      <p className="text-[11px] text-rose-700">
        {t("screens.connectorsView.failedToLoadHistory")}{" "}
        {history.error instanceof Error
          ? history.error.message
          : t("screens.connectorsView.unknown")}
      </p>
    );
  }
  const runs = history.data ?? [];
  if (runs.length === 0) {
    return (
      <p
        data-testid="connectors-history-empty"
        className="text-[11px] italic text-slate-500"
      >
        {t("screens.connectorsView.noRunsYetForThisConnector")}
      </p>
    );
  }
  return (
    <table data-testid="connectors-history" className="w-full text-[10px]">
      <thead>
        <tr className="border-b border-slate-200 text-start text-slate-500">
          <th className="px-2 py-1 font-semibold">
            {t("screens.connectorsView.started")}
          </th>
          <th className="px-2 py-1 font-semibold">
            {t("screens.connectorsView.trigger")}
          </th>
          <th className="px-2 py-1 font-semibold">
            {t("screens.connectorsView.status")}
          </th>
          <th className="px-2 py-1 font-semibold">
            {t("screens.connectorsView.total")}
          </th>
          <th className="px-2 py-1 font-semibold">{t("screens.connectorsView.ins")}</th>
          <th className="px-2 py-1 font-semibold">{t("screens.connectorsView.upd")}</th>
          <th className="px-2 py-1 font-semibold">
            {t("screens.connectorsView.skip")}
          </th>
          <th className="px-2 py-1 font-semibold">{t("screens.connectorsView.err")}</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => (
          <Fragment key={run.id}>
            <tr
              data-testid="connectors-history-row"
              data-run-status={run.status}
              className="border-b border-slate-100"
            >
              <td className="px-2 py-1 text-slate-700">
                {run.started_at ? (
                  <Figure>{formatDateTime(run.started_at, i18n.language)}</Figure>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-2 py-1 text-slate-700">{run.trigger_kind}</td>
              <td className="px-2 py-1">
                <span
                  className={
                    run.status === "succeeded"
                      ? "text-emerald-800"
                      : run.status === "failed"
                        ? "text-rose-800"
                        : "text-slate-700"
                  }
                >
                  {run.status}
                </span>
              </td>
              <td className="px-2 py-1 text-slate-700">{run.row_total}</td>
              <td className="px-2 py-1 text-slate-700">{run.row_inserted}</td>
              <td className="px-2 py-1 text-slate-700">{run.row_updated}</td>
              <td className="px-2 py-1 text-slate-700">{run.row_skipped}</td>
              <td className="px-2 py-1 text-slate-700">{run.error_count}</td>
            </tr>
            <SkipReasons reasons={run.skipped_by_reason} />
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

/**
 * P427 -- why a run skipped what it skipped. The first Archer run read
 * "1,862 skipped" with every reason the adapter gave thrown away. A
 * reason the catalogue has no words for is shown as the adapter's own
 * code, not dressed as a sentence nobody can translate.
 */
function SkipReasons({
  reasons,
}: {
  reasons: Record<string, number> | undefined;
}): JSX.Element | null {
  const { t } = useTranslation();
  const entries = Object.entries(reasons ?? {});
  if (entries.length === 0) return null;
  return (
    <tr data-testid="connectors-history-skips" className="border-b border-slate-100">
      <td colSpan={8} className="px-2 pb-1 text-[11px] text-slate-600">
        {t("screens.connectorsView.skippedBecause")}{" "}
        {entries.map(([reason, count], i) => (
          <span key={reason} data-reason={reason}>
            {i > 0 && " · "}
            <Figure>{String(groupedCount(count, i18n.language))}</Figure>{" "}
            {i18n.exists(`screens.connectorsView.skipReason.${reason}`) ? (
              t(`screens.connectorsView.skipReason.${reason}`)
            ) : (
              <code className="font-mono">{reason}</code>
            )}
          </span>
        ))}
      </td>
    </tr>
  );
}

// P49.2 (#579): inline cadence editor. Pencil button reveals a
// number input + a preview of "every N min/hour" or "manual only";
// Save fires the PATCH; Cancel reverts. Empty value = null = manual.
function CadenceCell({
  row,
  isToggling,
  onCadenceUpdate,
}: {
  row: ConnectorRow;
  isToggling: boolean;
  onCadenceUpdate: (next: number | null) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    row.cadence_seconds == null ? "" : String(row.cadence_seconds),
  );

  const previewSeconds: number | null = (() => {
    if (draft.trim() === "") return null;
    const n = Number(draft);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) return null;
    return n;
  })();
  const previewLabel =
    draft.trim() === ""
      ? "manual only"
      : previewSeconds == null
        ? "invalid"
        : formatCadence(previewSeconds, t);

  const onSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (draft.trim() === "") {
      onCadenceUpdate(null);
    } else {
      if (previewSeconds == null) return;
      onCadenceUpdate(previewSeconds);
    }
    setEditing(false);
  };

  const onCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(row.cadence_seconds == null ? "" : String(row.cadence_seconds));
    setEditing(false);
  };

  const onEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(row.cadence_seconds == null ? "" : String(row.cadence_seconds));
    setEditing(true);
  };

  if (!editing) {
    return (
      <span className="flex items-center gap-1">
        <span data-testid="connectors-row-cadence-display">
          {formatCadence(row.cadence_seconds, t)}
        </span>
        <button
          type="button"
          data-testid="connectors-row-cadence-edit"
          onClick={onEditClick}
          className="rounded border border-slate-300 bg-white px-1.5 py-0 text-[9px] font-semibold text-slate-500 hover:text-slate-800"
          title={t("screens.connectorsView.editCadence")}
        >
          {t("screens.connectorsView.edit")}
        </button>
      </span>
    );
  }

  const invalid = draft.trim() !== "" && previewSeconds == null;

  return (
    <span
      className="flex flex-wrap items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="number"
        inputMode="numeric"
        data-testid="connectors-row-cadence-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="manual"
        className="w-20 rounded border border-slate-300 px-1 py-0 text-[10px]"
      />
      <span
        data-testid="connectors-row-cadence-preview"
        className={`text-[10px] ${invalid ? "text-rose-700" : "text-slate-500"}`}
      >
        {previewLabel}
      </span>
      <button
        type="button"
        data-testid="connectors-row-cadence-save"
        disabled={isToggling || invalid}
        onClick={onSave}
        className="rounded border border-emerald-400 bg-emerald-50 px-1.5 py-0 text-[9px] font-semibold text-emerald-900 disabled:opacity-50"
      >
        {t("screens.connectorsView.save")}
      </button>
      <button
        type="button"
        data-testid="connectors-row-cadence-cancel"
        onClick={onCancel}
        className="rounded border border-slate-300 bg-white px-1.5 py-0 text-[9px] font-semibold text-slate-700"
      >
        {t("screens.connectorsView.cancel")}
      </button>
    </span>
  );
}

// P60.2 (#551): inline config view + editor. View mode renders the
// JSONB blob as a pretty-printed read-only block; Edit mode swaps to
// a <textarea dir="auto"> for whole-object replacement + Save / Cancel buttons.
// JSON parse failure disables Save. Whole-object semantics match the
// backend PATCH contract -- no partial merge.
function ConnectorConfigCell({ row }: { row: ConnectorRow }) {
  const { t } = useTranslation();
  const update = useUpdateConnector();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const initial = useMemo(
    () => (row.config == null ? "" : JSON.stringify(row.config, null, 2)),
    [row.config],
  );
  const [draft, setDraft] = useState<string>(initial);

  const { parsed, parseError } = useMemo<{
    parsed: Record<string, unknown> | null;
    parseError: string | null;
  }>(() => {
    if (draft.trim() === "") return { parsed: null, parseError: null };
    try {
      const v = JSON.parse(draft);
      if (v !== null && (typeof v !== "object" || Array.isArray(v))) {
        return { parsed: null, parseError: "config must be a JSON object" };
      }
      return { parsed: v as Record<string, unknown> | null, parseError: null };
    } catch (e) {
      return {
        parsed: null,
        parseError: e instanceof Error ? e.message : "invalid JSON",
      };
    }
  }, [draft]);

  const onEdit = () => {
    setDraft(initial);
    setEditing(true);
  };
  const onCancel = () => {
    setDraft(initial);
    setEditing(false);
  };
  const onSave = () => {
    if (parseError) return;
    update.mutate(
      { connectorId: row.id, config: parsed },
      {
        onSuccess: (data) => {
          if (data.config_changed) {
            toast.push({
              tone: "success",
              message: `Config updated: ${row.label}`,
              detail: `Connector kind: ${row.kind}`,
            });
          }
          setEditing(false);
        },
        onError: (err) => {
          toast.push({
            tone: "error",
            message: `Config update failed: ${row.label}`,
            detail: err.message,
          });
        },
      },
    );
  };

  return (
    <div
      data-testid="connectors-row-config"
      className="mb-3 rounded border border-slate-200 bg-white px-2 py-1.5"
    >
      <div className="mb-1 flex items-center justify-between text-[10px] text-slate-600">
        <span className="font-semibold uppercase tracking-wider">
          {t("screens.connectorsView.configNonSecret")}
        </span>
        {!editing && (
          <button
            type="button"
            data-testid="connectors-row-config-edit"
            onClick={onEdit}
            className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700"
          >
            {t("screens.connectorsView.edit")}
          </button>
        )}
      </div>
      {!editing ? (
        <pre
          data-testid="connectors-row-config-display"
          className="overflow-auto text-[10px] text-slate-700"
        >
          {row.config == null ? (
            <span className="italic text-slate-400">
              {t("screens.connectorsView.noConfig")}
            </span>
          ) : (
            JSON.stringify(row.config, null, 2)
          )}
        </pre>
      ) : (
        <div className="space-y-1">
          <textarea
            dir="auto"
            data-testid="connectors-row-config-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("screens.connectorsView.configPlaceholder")}
            rows={6}
            className="w-full rounded border border-slate-300 bg-slate-50 px-2 py-1 font-mono text-[10px]"
          />
          {parseError && (
            <p
              data-testid="connectors-row-config-error"
              className="text-[10px] text-rose-700"
            >
              {parseError}
            </p>
          )}
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="connectors-row-config-save"
              disabled={parseError !== null || update.isPending}
              onClick={onSave}
              className="rounded border border-emerald-400 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-900 disabled:opacity-50"
            >
              {update.isPending
                ? t("screens.connectorsView.saving")
                : t("screens.connectorsView.save")}
            </button>
            <button
              type="button"
              data-testid="connectors-row-config-cancel"
              onClick={onCancel}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700"
            >
              {t("screens.connectorsView.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// P213.2: per-connector credential store. Renders nothing for connector
// kinds that have no credential spec (SNOW / Tenable / generic test rows).
function ConnectorCredentialsCell({ row }: { row: ConnectorRow }) {
  const { t } = useTranslation();
  const creds = useConnectorCredentials(row.id);
  const test = useTestConnector();
  const toast = useToast();
  const [result, setResult] = useState<ConnectorTestResult | null>(null);
  const data = creds.data;
  // A payload without `fields` crashed the whole expanded panel -- and
  // took the fault message, the escalation line and the run history
  // down with it. A missing list is an empty one; there is nothing to
  // render either way.
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  if (creds.isLoading || creds.error || !data || fields.length === 0) {
    return null;
  }

  const onTest = () => {
    setResult(null);
    test.mutate(row.id, {
      onSuccess: (res) => {
        setResult(res);
        toast.push({
          tone: res.ok ? "success" : "warning",
          message: res.ok
            ? `Connected in ${res.latency_ms}ms`
            : `Test failed: ${row.label}`,
          detail: res.ok
            ? `First batch: ${res.records_in_first_batch ?? 0} record(s)`
            : (res.error ?? "connection failed"),
        });
      },
      onError: (err) =>
        toast.push({
          tone: "error",
          message: `Cannot test: ${row.label}`,
          detail: err.message,
        }),
    });
  };

  return (
    <div
      data-testid="connectors-row-credentials"
      className="mb-3 rounded border border-slate-200 bg-white px-2 py-1.5"
    >
      <div className="mb-1 flex items-center justify-between text-[10px] text-slate-600">
        <span className="font-semibold uppercase tracking-wider">
          {t("screens.connectorsView.credentials")}
        </span>
        <div className="flex items-center gap-2">
          {!data.encryption_configured && (
            <span
              data-testid="connectors-cred-no-key"
              className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800"
            >
              {t("screens.connectorsView.encryptionKeyNotSet")}
            </span>
          )}
          {test.isPending ? (
            <EngineBusy
              label={t("screens.connectorsView.testingConnection")}
              testid="connectors-cred-testing"
            />
          ) : (
            <button
              type="button"
              onClick={onTest}
              data-testid="connectors-cred-test"
              className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-[10px] font-semibold text-brand-brassDark"
            >
              {t("screens.connectorsView.testConnection")}
            </button>
          )}
        </div>
      </div>
      {result && (
        <p
          data-testid="connectors-cred-test-result"
          className={`mb-1.5 text-[10px] ${result.ok ? "text-emerald-700" : "text-rose-700"}`}
        >
          {result.ok
            ? `✓ Connected in ${result.latency_ms}ms (first batch: ${result.records_in_first_batch ?? 0} records)`
            : `✗ ${result.error ?? "test failed"}`}
        </p>
      )}
      {!data.encryption_configured && (
        <p className="mb-1.5 text-[10px] text-amber-700">
          {t("screens.connectorsView.secretsCanAposTBeStoredUntil")}
        </p>
      )}
      <div className="flex flex-col gap-1">
        {fields.map((field) => (
          <CredentialFieldRow
            key={field.name}
            connectorId={row.id}
            connectorLabel={row.label}
            field={field}
            encryptionConfigured={data.encryption_configured}
          />
        ))}
      </div>
    </div>
  );
}

function CredentialFieldRow({
  connectorId,
  connectorLabel,
  field,
  encryptionConfigured,
}: {
  connectorId: string;
  connectorLabel: string;
  field: ConnectorCredentialField;
  encryptionConfigured: boolean;
}) {
  const { t } = useTranslation();
  const set = useSetConnectorCredential();
  const clear = useClearConnectorCredential();
  const toast = useToast();
  const [draft, setDraft] = useState("");

  const onSet = () => {
    if (!draft) return;
    set.mutate(
      { connectorId, fieldName: field.name, value: draft },
      {
        onSuccess: () => {
          setDraft("");
          toast.push({
            tone: "success",
            message: `${field.label} stored`,
            detail: connectorLabel,
          });
        },
        onError: (err) =>
          toast.push({
            tone: "error",
            message: `Could not store ${field.label}`,
            detail: err.message,
          }),
      },
    );
  };

  const onClear = () => {
    clear.mutate(
      { connectorId, fieldName: field.name },
      {
        onSuccess: () =>
          toast.push({ tone: "success", message: `${field.label} cleared` }),
        onError: (err) =>
          toast.push({
            tone: "error",
            message: `Could not clear ${field.label}`,
            detail: err.message,
          }),
      },
    );
  };

  const pillClass =
    field.status === "stored"
      ? "bg-emerald-100 text-emerald-800"
      : "bg-slate-100 text-slate-600";
  const pillText =
    field.status === "stored"
      ? "stored"
      : field.status === "env"
        ? "from env"
        : "not set";

  return (
    <div
      data-testid={`connectors-cred-field-${field.name}`}
      className="flex items-center gap-2 text-[11px]"
    >
      <span className="w-44 shrink-0 text-slate-700">
        {field.label}
        {field.required && (
          <span className="text-rose-500">{t("screens.connectorsView.text")}</span>
        )}
        {field.secret && (
          <span className="ms-1 text-[9px] uppercase text-slate-400">
            {t("screens.connectorsView.secret")}
          </span>
        )}
      </span>
      <span
        data-testid={`connectors-cred-status-${field.name}`}
        className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${pillClass}`}
      >
        {pillText}
      </span>
      <input
        dir="auto"
        type={field.secret ? "password" : "text"}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={field.secret ? "enter to set" : field.name}
        data-testid={`connectors-cred-input-${field.name}`}
        className="w-48 rounded border border-slate-300 px-1.5 py-0.5 text-[10px]"
      />
      <button
        type="button"
        disabled={!draft || !encryptionConfigured || set.isPending}
        onClick={onSet}
        data-testid={`connectors-cred-set-${field.name}`}
        className="rounded bg-brand-brass px-2 py-0.5 text-[10px] font-semibold text-brand-navy disabled:opacity-50"
      >
        {set.isPending ? "…" : t("screens.connectorsView.set")}
      </button>
      {field.status === "stored" && (
        <button
          type="button"
          onClick={onClear}
          data-testid={`connectors-cred-clear-${field.name}`}
          className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700"
        >
          {t("screens.connectorsView.clear")}
        </button>
      )}
    </div>
  );
}
